import React from 'react';
import { Button } from '@/components/ui/button';
import { Flask, PaperPlaneTilt } from '@phosphor-icons/react';
import RequisitionDeleteButton from '@/components/RequisitionDeleteButton';

export const LabRequisitionTable = ({ data, showActions = false, allQuots, onSendRfq, onViewQuotations, onDeleted }) => {
  const rfqProgress = (req) => {
    const hist = req.rfq_history || [];
    if (hist.length === 0) return null;
    const rfqVendors = [...new Set(hist.map((h) => (h.vendor_name || '').toLowerCase().trim()))];
    const quotVendors = new Set(
      allQuots.filter((q) => q.requisition_id === req.id).map((q) => (q.vendor_name || '').toLowerCase().trim())
    );
    const answered = rfqVendors.filter((v) => quotVendors.has(v)).length;
    return { answered, total: rfqVendors.length };
  };

  return (
    <div className="bg-white border border-border-default rounded">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface border-b border-border-default">
            <tr>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Reagent</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Employee</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Vendor</th>
              <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Qty</th>
              <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Status</th>
              {showActions && <th className="text-right p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.map((req) => (
              <tr key={req.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`lab-requisition-${req.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-brand-subtle flex items-center justify-center">
                      <Flask size={20} className="text-accent-brand" weight="duotone" />
                    </div>
                    <div>
                      <div className="font-medium text-text-primary">{req.reagent_name}</div>
                      <div className="text-sm text-text-tertiary">{req.catalogue_number}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-text-secondary">{req.employee_name}</td>
                <td className="p-4 text-text-secondary">{req.vendor_name}</td>
                <td className="p-4 text-text-secondary text-right font-medium" data-testid={`lab-req-quantity-${req.id}`}>{req.quantity ?? 1}</td>
                <td className="p-4">
                  <span className="status-badge status-pending">{req.status.replace(/_/g, ' ')}</span>
                </td>
                {showActions && (
                  <td className="p-4 text-right">
                    <div className="flex gap-2 justify-end items-center">
                      {(() => {
                        const p = rfqProgress(req);
                        if (!p) return null;
                        const done = p.answered >= p.total;
                        return (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                              done
                                ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                                : 'bg-amber-100 text-amber-800 border-amber-300'
                            }`}
                            title={`${p.answered} of ${p.total} RFQ'd vendor${p.total === 1 ? '' : 's'} have submitted a quote`}
                            data-testid={`rfq-progress-${req.id}`}
                          >
                            {p.answered}/{p.total} RFQs answered
                          </span>
                        );
                      })()}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSendRfq(req)}
                        data-testid={`send-rfq-${req.id}`}
                      >
                        <PaperPlaneTilt size={16} className="mr-1" />
                        RFQ
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewQuotations(req)}
                        data-testid={`view-quotations-${req.id}`}
                      >
                        View Quotations
                      </Button>
                      <RequisitionDeleteButton requisition={req} onDeleted={onDeleted} />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LabRequisitionTable;
